/**
 * TripleLineChart Component
 *
 * Displays three metrics as lines on a single shared Y-axis using Recharts LineChart.
 * Used for actual vs plan vs prior year comparisons (e.g. Revenue vs Plan & Prior Year).
 *
 * Colours:
 *   - Actual   : #3498db (blue solid)
 *   - Plan     : #f39c12 (amber dashed)
 *   - Prior Year: #95a5a6 (grey dashed)
 *
 * Performance: Uses ChartDataContext for cached data — no individual API calls.
 */

import PropTypes from 'prop-types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartData } from '../../contexts/ChartDataContext';
import { formatDate, formatMetricValue, formatAxisValue } from '../../utils/formatters';
import './ChartStyles.css';

const ACTUAL_COLOR = '#3498db';
const PLAN_COLOR = '#f39c12';
const PRIOR_YEAR_COLOR = '#95a5a6';

const TripleLineChart = ({ chartId }) => {
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);
  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="spinner"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className="chart-container no-data">
        <p>No data available</p>
      </div>
    );
  }

  const {
    chart_name,
    data_points,
    actual_metric,
    plan_metric,
    prior_year_metric,
  } = data;

  const sharedUnit = actual_metric?.metric_unit;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(label, 'short')}</p>
          {payload.map((entry, index) => {
            if (entry.value == null) return null;
            let metricName = '';
            if (entry.dataKey === 'actual_value') metricName = actual_metric?.metric_name || 'Actual';
            else if (entry.dataKey === 'plan_value') metricName = plan_metric?.metric_name || 'Plan';
            else if (entry.dataKey === 'prior_year_value') metricName = prior_year_metric?.metric_name || 'Prior Year';
            return (
              <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                {metricName}: {formatMetricValue(entry.value, sharedUnit)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    // Detect weekly granularity: check average gap between first few sorted data points
    const sorted = [...data_points].filter(p => p.date).sort((a, b) => new Date(a.date) - new Date(b.date));
    let isWeekly = false;
    if (sorted.length >= 3) {
      const gap1 = (new Date(sorted[1].date) - new Date(sorted[0].date)) / (1000 * 60 * 60 * 24);
      const gap2 = (new Date(sorted[2].date) - new Date(sorted[1].date)) / (1000 * 60 * 60 * 24);
      const avgGap = (gap1 + gap2) / 2;
      isWeekly = avgGap >= 5 && avgGap <= 10; // ~7 day intervals
    }

    if (isWeekly) {
      // Show day + abbreviated month for weekly data (e.g. "7 Dec", "14 Dec")
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }

    const firstDate = new Date(sorted[0]?.date);
    const lastDate = new Date(sorted[sorted.length - 1]?.date);
    const timeSpanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    if (timeSpanDays <= 90) return formatDate(dateStr, 'short');
    if (timeSpanDays <= 730) return formatDate(dateStr, 'month');
    return formatDate(dateStr, 'year');
  };

  const sortedData = [...data_points].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Build subtitle from available metric names
  const subtitleParts = [
    actual_metric?.metric_name,
    plan_metric?.metric_name,
    prior_year_metric?.metric_name,
  ].filter(Boolean);
  const subtitle = subtitleParts.join(' vs ');

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{chart_name}</h3>
        {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      </div>

      <div className="chart-body">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={sortedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, sharedUnit)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* Actual Revenue — blue solid */}
            <Line
              type="monotone"
              dataKey="actual_value"
              name={actual_metric?.metric_name || 'Actual'}
              stroke={ACTUAL_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />

            {/* Plan — amber dashed (only if plan_metric provided) */}
            {plan_metric && (
              <Line
                type="monotone"
                dataKey="plan_value"
                name={plan_metric.metric_name || 'Plan'}
                stroke={PLAN_COLOR}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            )}

            {/* Prior Year — grey dashed (only if prior_year_metric provided) */}
            {prior_year_metric && (
              <Line
                type="monotone"
                dataKey="prior_year_value"
                name={prior_year_metric.metric_name || 'Prior Year'}
                stroke={PRIOR_YEAR_COLOR}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

TripleLineChart.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default TripleLineChart;
